import type { ApolloError } from '@apollo/client'
import type { Moment } from 'moment'
import type React from 'react'
import styled from 'styled-components'

import Loading from './Loading'
import Pagination from './Pagination'
import type { SelectOption } from './Select'
import { toSelectOption } from './Select'

const ResultsContainer = styled.div`
  display: grid;
  grid-template-rows: min-content 1fr min-content;
  min-height: 0;
`

const ScrollContainer = styled.div`
  margin-top: 10px;
  overflow-y: scroll;
`

const RowContainer = styled.div<{ margin: boolean }>`
  margin-top: ${props => (props.margin ? '20px' : 0)};
`

const ResultsInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const PaginationContainer = styled.div`
  margin-top: 20px;
`

const ResultsMessage = styled.div`
  display: grid;
  place-items: center;
  img {
    max-width: 300px;
  }
`

const limitSelectOptions: SelectOption[] = ['5', '10', '25', '50', '100'].map(
  toSelectOption,
)

interface Props<T extends {}> {
  loading: boolean
  error?: ApolloError
  lastUpdated: Moment
  limit: number
  offset: number
  onChangePage: (limit: number, offset: number) => void
  total: number
  data: T[]
  row: (data: T) => React.ReactNode
}

const PaginatedList = <T extends {}>({
  loading,
  error,
  data,
  total,
  lastUpdated,
  limit,
  offset,
  onChangePage,
  row,
}: Props<T>) => (
  <ResultsContainer>
    {loading && total === 0 ? (
      <div>
        <Loading />
      </div>
    ) : data !== undefined && data.length > 0 ? (
      <>
        <ResultsInfoContainer>
          <div>
            <em>Found {total} results</em>
          </div>
          <div>
            <em>
              Last refreshed on {lastUpdated.format('Do MMMM')} at{' '}
              {lastUpdated.format('h:mm:ss a')}
            </em>
          </div>
        </ResultsInfoContainer>
        <ScrollContainer>
          {data.map((data, i) => (
            <RowContainer margin={i !== 0} key={i}>
              {row(data)}
            </RowContainer>
          ))}
        </ScrollContainer>
        {total > 1 && (
          <PaginationContainer>
            <Pagination
              total={total}
              limitSelectOptions={limitSelectOptions}
              limit={limit}
              onChangePage={onChangePage}
              offset={offset}
            />
          </PaginationContainer>
        )}
      </>
    ) : error !== undefined ? (
      <ResultsMessage>
        <h5>An unexpected error occurred.</h5>
      </ResultsMessage>
    ) : (
      <ResultsMessage>
        <h5>
          Nothing here! Your applications will appear here once you have created your
          first one.
        </h5>
      </ResultsMessage>
    )}
  </ResultsContainer>
)

export default PaginatedList
